import React, { forwardRef } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import { LineChart } from "echarts/charts";
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
} from "echarts/components";
import * as echarts from "echarts/core";
import { rgbaColor } from "../../../../../../helpers/utils";
import { useAppContext } from "../../../../../../providers/AppProvider";
import ReactEchart from "../../../../../../components/common/ReactEchart";
import { isNaN } from "underscore";

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LineChart,
  LegendComponent,
]);

const tooltipFormatter = (params) => {
  let tooltip = `<div>${params[0].name}</div>`;
  tooltip += params
    .map(
      ({ value, borderColor, seriesName }, index) =>
        `   
        <div class="dot me-1 fs-11 d-inline-block" style="background-color: ${borderColor}"></div>
        <span class='text-600'>${seriesName}: ${!isNaN(value)
          ? index === 0
            ? parseFloat(value).toFixed(2)
            : parseFloat(value).toFixed(0)
          : 0
        }</span>
        `
    )
    .join("<br/>");
  return tooltip;
};

const velMaxDiarias = (data, setVelMaxima) => {
  const maximasPorDia = [];
  let maximaAbsoluta = 0;

  for (const fecha in data) {
    const registros = Array.isArray(data[fecha]) ? data[fecha] : [];
    if (registros.length === 0) {
      maximasPorDia.push(0); // No grafica nada para ese día
      continue;
    }

    const velocidades = registros
      .filter(registro =>
        (registro.tipoMotor === 1 && [1, 2, 4, 6].includes(registro.tipo)) ||
        (registro.tipoMotor === 0 && [3, 5].includes(registro.tipo))
      )
      .map(registro => registro.velocidad)
      .filter(v => !isNaN(v));

    if (velocidades.length === 0) {
      maximasPorDia.push(0); // No grafica si no hay velocidades válidas
      continue;
    }

    const maximaDia = Math.max(...velocidades);
    maximasPorDia.push(maximaDia);

    if (maximaDia > maximaAbsoluta) {
      maximaAbsoluta = maximaDia;
    }
  }


  return maximasPorDia;
};

const getOptions = (
  getThemeColor,
  data,
  isSingleDay,
  promedioPorFecha,
  maximasPorDia,
  maximaPorFecha
) => ({
  color: getThemeColor("gray-100"),
  tooltip: {
    trigger: "axis",
    padding: [7, 10],
    backgroundColor: getThemeColor("gray-100"),
    borderColor: getThemeColor("gray-100"),
    textStyle: { color: getThemeColor("gray-1100") },
    borderWidth: 1,
    formatter: tooltipFormatter,
    transitionDuration: 0,
  },
  legend: {
    data: ["Velocidad promedio", "Velocidad máxima"],
    show: false,
  },
  xAxis: {
    type: "category",
    data: Object.keys(data),
    boundaryGap: false,
    axisPointer: {
      lineStyle: {
        color: getThemeColor("gray-300"),
        type: "dashed",
      },
    },
    splitLine: { show: false },
    axisLine: {
      lineStyle: {
        color: rgbaColor("#000", 0.01),
        type: "dashed",
      },
    },
    axisTick: { show: false },
    axisLabel: {
      color: getThemeColor("gray-400"),
      margin: 15,
      rotate: Object.keys(data).length > 6 ? 45 : 0,
      /*  formatter: (value) =>
         isSingleDay ? `${value} hrs` : dayjs(value).format("MMM DD"), */
      formatter: (value) => {
        if (isSingleDay) return `${value} hr(s)`;
        const fecha = dayjs(value);
        const dias = ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa'];
        const diaSemana = dias[fecha.day()];
        return `${diaSemana} ${fecha.format("DD MMM")}`;
      },

    },

  },
  yAxis: {
    type: "value",
    axisPointer: { show: false },
    splitLine: {
      lineStyle: {
        color: getThemeColor("gray-300"),
        type: "dashed",
      },
    },
    boundaryGap: false,
    axisLabel: {
      show: true,
      color: getThemeColor("gray-400"),
      margin: 15,
    },
    axisTick: { show: false },
    axisLine: { show: false },
  },
  series: [
    {
      name: "Vel. diaria promedio",
      type: "line",
      data: promedioPorFecha,
      lineStyle: { color: getThemeColor("primary") },
      itemStyle: {
        borderColor: getThemeColor("primary"),
        borderWidth: 2,
        backgroundColor: "transparent",
      },
      symbol: "circle",
      symbolSize: 10,
      emphasis: {
        scale: true,
      },
      areaStyle: {
        color: {
          type: "linear",
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            {
              offset: 0,
              color: rgbaColor(getThemeColor("primary"), 0.2),
            },
            {
              offset: 1,
              color: rgbaColor(getThemeColor("primary"), 0),
            },
          ],
        },
      },
    },
    {
      name: "Vel. máxima",
      type: "line",
      data: maximasPorDia,
      lineStyle: {
        color: rgbaColor(getThemeColor("danger"), 0.7),
        width: 2,
        type: 'transparent'
      },
      itemStyle: {
        borderColor: rgbaColor(getThemeColor("danger"), 0.9),
        borderWidth: 3,
      },
      symbol: 'circle',
      symbolSize: 12,
      symbolSize: 10,
      emphasis: {
        scale: true,
      },
      areaStyle: {
        color: {
          type: "linear",
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            {
              offset: 0,
              color: rgbaColor(getThemeColor("primary"), 0.2),
            },
            {
              offset: 1,
              color: rgbaColor(getThemeColor("primary"), 0),
            },
          ],
        },
      },
    },
    {
      name: "Lím. velocidad",
      type: "line",
      data: maximaPorFecha,
      lineStyle: { color: rgbaColor(getThemeColor("warning"), 0.3) },
      itemStyle: {
        borderColor: rgbaColor(getThemeColor("warning"), 0.6),
        borderWidth: 2,
      },
      symbol: "circle",
      symbolSize: 10,
      emphasis: {
        scale: true,
      },
    },
  ],
  grid: { right: "38px", left: "45px", bottom: "20%", top: "5%" },
});

const VelocidadChart = forwardRef(
  ({ data, isSingleDay, promedioPorFecha, maximasPorDia, maximaPorFecha }, ref) => {
    const { getThemeColor } = useAppContext();

    return (

      <ReactEchart
        ref={ref}
        echarts={echarts}
        style={{ width: '100%', height: '90%' }}
        option={getOptions(
          getThemeColor,
          data,
          isSingleDay,
          promedioPorFecha,
          maximasPorDia,
          maximaPorFecha
        )}

      />
    );
  }
);

VelocidadChart.propTypes = {
  data: PropTypes.object.isRequired,
  isSingleDay: PropTypes.bool,
  promedioPorFecha: PropTypes.array,
  maximasPorDia: PropTypes.array,
  maximaPorFecha: PropTypes.array,
};

export default VelocidadChart;




// import React, { forwardRef } from "react";
// import PropTypes from "prop-types";
// import dayjs from "dayjs";
// import { LineChart } from "echarts/charts";
// import {
//   GridComponent,
//   LegendComponent,
//   TitleComponent,
//   TooltipComponent,
// } from "echarts/components";
// import * as echarts from "echarts/core";
// import { rgbaColor } from "../../../../../../helpers/utils";
// import { useAppContext } from "../../../../../../providers/AppProvider";
// import ReactEchart from "../../../../../../components/common/ReactEchart";
// import { isNaN, isNumber } from "underscore";

// echarts.use([
//   TitleComponent,
//   TooltipComponent,
//   GridComponent,
//   LineChart,
//   LegendComponent,
// ]);

// const tooltipFormatter = (params) => {

//   let tooltip = `<div>${params[0].name}</div>`

//   tooltip += params.map(({ value, borderColor, seriesName }, index) =>
//     `
//         <div class="dot me-1 fs-11 d-inline-block" style="background-color: ${borderColor}"></div>
//         <span class='text-600'>${seriesName}: ${(!isNaN(value)) ? (index === 0) ? parseFloat(value).toFixed(2) : parseFloat(value).toFixed(0) : 0}</span>
//         `
//   )
//     .join('<br/>');

//   return tooltip;
// };

// const velProm = (data, setVelDiariaProm) => {
//   // Calculamos el promedio de velocidad por fecha
//   const promedios = [];
//   const velocidades = []

//   for (const fecha in data) {
//     const vehiculos = Array.isArray(data[fecha]) ? data[fecha] : []; // Asegurar que es un array

//     if (vehiculos.length === 0) continue; // Evitar cálculos innecesarios

//     let promedio_diario = [];

//     vehiculos.forEach((p) => {

//       if ((p.tipo === 1 || p.tipo == 2 || p.tipo === 4 || p.tipo == 6) && p.tipoMotor == 1) {

//         promedio_diario.push(p.velocidad);
//         velocidades.push(p.velocidad);
//       } else if ((p.tipo === 3 || p.tipo == 5) && p.tipoMotor == 0) {

//         promedio_diario.push(p.velocidad);
//         velocidades.push(p.velocidad);
//       }
//     })

//     // Calcula el promedio de la velocidad
//     const totalVelocidad = promedio_diario.reduce(
//       (sum, item) => sum + item,
//       0
//     );

//     const promedio = totalVelocidad / promedio_diario.length;
//     promedios.push(promedio);
//   }

//   const promVelDiaria =
//     velocidades.length > 0
//       ? (velocidades.reduce((sum, item) => sum + item, 0) / velocidades.length).toFixed(2)
//       : 0; // Evitar división por 0

//   setVelDiariaProm(promVelDiaria);

//   return promedios;
// };

// const velMax = (data, setVelMaxima) => {
//   const velocidadesMaximas = [];
//   for (const fecha in data) {
//     const vehiculos = Array.isArray(data[fecha]) ? data[fecha] : [];
//     if (vehiculos.length === 0) continue;

//     // Velocidad Maxima
//     const totalMaxima = vehiculos.reduce(
//       (sum, vehiculo) => sum + (vehiculo.vehiculos.velocidadMaxima || 0),
//       0
//     );
//     const maxima = Math.round(totalMaxima / vehiculos.length);
//     velocidadesMaximas.push(maxima);
//   }
//   const velMax =
//     velocidadesMaximas.length > 0
//       ? Math.round(
//         velocidadesMaximas.reduce((sum, item) => sum + item, 0) /
//         velocidadesMaximas.length
//       )
//       : 0;
//   setVelMaxima(velMax);
//   return velocidadesMaximas;
// };

// const getOptions = (
//   getThemeColor,
//   data,
//   isSingleDay,
//   setVelDiariaProm,
//   setVelMaxima
// ) => ({
//   color: getThemeColor("gray-100"),
//   tooltip: {
//     trigger: "axis",
//     padding: [7, 10],
//     backgroundColor: getThemeColor("gray-100"),
//     borderColor: getThemeColor("gray-100"),
//     textStyle: { color: getThemeColor("gray-1100") },
//     borderWidth: 1,
//     formatter: tooltipFormatter,
//     transitionDuration: 0,
//   },
//   legend: {
//     data: ["Velocidad promedio", "Velocidad máxima"],
//     show: false,
//   },
//   xAxis: {
//     type: "category",
//     data: Object.keys(data),
//     boundaryGap: false,
//     axisPointer: {
//       lineStyle: {
//         color: getThemeColor("gray-300"),
//         type: "dashed",
//       },
//     },
//     splitLine: { show: false },
//     axisLine: {
//       lineStyle: {
//         color: rgbaColor("#000", 0.01),
//         type: "dashed",
//       },
//     },
//     axisTick: { show: false },
//     axisLabel: {
//       color: getThemeColor("gray-400"),
//       margin: 15,
//       formatter: (value) =>
//         isSingleDay ? `${value} hrs` : dayjs(value).format("MMM DD"),
//     },
//   },
//   yAxis: {
//     type: "value",
//     axisPointer: { show: false },
//     splitLine: {
//       lineStyle: {
//         color: getThemeColor("gray-300"),
//         type: "dashed",
//       },
//     },
//     boundaryGap: false,
//     axisLabel: {
//       show: true,
//       color: getThemeColor("gray-400"),
//       margin: 15,
//     },
//     axisTick: { show: false },
//     axisLine: { show: false },
//   },
//   series: [
//     {
//       name: "Velocidad promedio",
//       type: "line",
//       data: velProm(data, setVelDiariaProm),
//       lineStyle: { color: getThemeColor("primary") },
//       itemStyle: {
//         borderColor: getThemeColor("primary"),
//         borderWidth: 2,
//         backgroundColor: "transparent",
//       },
//       symbol: "circle",
//       symbolSize: 10,
//       emphasis: {
//         scale: true,
//       },
//       areaStyle: {
//         color: {
//           type: "linear",
//           x: 0,
//           y: 0,
//           x2: 0,
//           y2: 1,
//           colorStops: [
//             {
//               offset: 0,
//               color: rgbaColor(getThemeColor("primary"), 0.2),
//             },
//             {
//               offset: 1,
//               color: rgbaColor(getThemeColor("primary"), 0),
//             },
//           ],
//         },
//       },
//     },
//     {
//       name: "Velocidad máxima",
//       type: "line",
//       data: velMax(data, setVelMaxima),
//       lineStyle: { color: rgbaColor(getThemeColor("warning"), 0.3) },
//       itemStyle: {
//         borderColor: rgbaColor(getThemeColor("warning"), 0.6),
//         borderWidth: 2,
//       },
//       symbol: "circle",
//       symbolSize: 10,
//       emphasis: {
//         scale: true,
//       },
//     },
//   ],
//   grid: { right: "18px", left: "40px", bottom: "15%", top: "5%" },
// });

// const VelocidadChart = forwardRef(
//   ({ data, isSingleDay, setVelDiariaProm, setVelMaxima }, ref) => {
//     const { getThemeColor } = useAppContext();
//     return (
//       <ReactEchart
//         ref={ref}
//         echarts={echarts}
//         option={getOptions(
//           getThemeColor,
//           data,
//           isSingleDay,
//           setVelDiariaProm,
//           setVelMaxima
//         )}
//       />
//     );
//   }
// );

// VelocidadChart.propTypes = {
//   data: PropTypes.shape({
//     velocidadMaxima: PropTypes.array,
//     velocidadPromedio: PropTypes.array,
//     fechas: PropTypes.array,
//   }),
// };

// export default VelocidadChart;
